// Human-readable labels for the raw enum/code values the API uses.
// Filters/badges/cells should show these; the underlying value stays the enum.

// Tokens that should render upper-cased when they appear as a word.
const ACRONYMS = new Set([
  "ceo",
  "sos",
  "vfd",
  "latra",
  "dpo",
  "kyc",
  "id",
  "vat",
  "otp",
  "url",
]);

// Explicit overrides for values that don't humanize cleanly.
const OVERRIDES: Record<string, string> = {
  // trip status
  inProgress: "In Progress",
  driverArrived: "Driver Arrived",
  intermediateStopArrived: "Stop Arrived",
  intermediateStopSkipped: "Stop Skipped",
  destinationArrived: "Destination Arrived",
  notFound: "Not Found",
  // payment / transaction
  walletTopUp: "Wallet Top-up",
  WALLET_TOPUP: "Wallet Top-up",
  TRIP_PAYMENT: "Trip Payment",
  DRIVER_PAYOUT: "Driver Payout",
  // admin roles
  branchManager: "Branch Manager",
  registrationTeam: "Registration Team",
  operationsTeam: "Operations Team",
  supportTeam: "Support Team",
  // driver availability
  underTrip: "On Trip",
  underApproval: "Under Approval",
  documentPending: "Documents Pending",
};

function titleizeWord(w: string): string {
  if (!w) return w;
  if (ACRONYMS.has(w.toLowerCase())) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/**
 * Humanize a raw enum/code value: camelCase / snake_case / kebab / UPPER → Title Case.
 * `inProgress` → "In Progress", `CASH` → "Cash", `in_progress` → "In Progress".
 */
export function humanize(value: string | undefined | null): string {
  if (value === undefined || value === null || value === "") return "—";
  const raw = String(value);
  if (OVERRIDES[raw]) return OVERRIDES[raw];

  const spaced = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase → camel Case
    .replace(/[_-]+/g, " ") // snake/kebab → space
    .trim();

  return spaced
    .split(/\s+/)
    .map(titleizeWord)
    .join(" ");
}

// Convenience aliases (all just delegate to humanize, kept for call-site clarity).
export const tripStatusLabel = humanize;
export const paymentModeLabel = humanize;
export const adminRoleLabel = humanize;
export const txnTypeLabel = humanize;
export const txnStatusLabel = humanize;

/** Build Mantine Select data from raw enum values: { value: raw, label: humanized }. */
export function toSelectOptions(
  values: readonly string[],
  withAll?: string,
): { value: string; label: string }[] {
  const opts = values.map((v) => ({ value: v, label: humanize(v) }));
  return withAll ? [{ value: "", label: withAll }, ...opts] : opts;
}
